import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import PoliciesBalance from '../../../../core/data/models/policies.balance.model';
import { PaymentService } from '../../services/payment.service';
import { PaymentTokenService } from '../../services/payment.token.service';
import { SwalService } from '../../../../common/services/swal.service';
import PolicyInfo from '../../../../core/data/models/policy.info.model';
import { DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import { LocalStoreService } from '../../../../common/services/storage.service';
import keys from '../../../../common/constants/keys';
import { SharedModule } from '../../../../common/shared.module';
import { policyMask } from '../../../../common/constants/masks';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import PolicyBalance from '../../../../core/data/models/policy.balance.model';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';

@Component({
  selector: 'app-payments',
  imports: [
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    DecimalPipe,
    SharedModule,
    NgxMaskDirective,
    NgxMaskPipe,
  ],
  providers: [PaymentTokenService, NgxMaskPipe],
  templateUrl: './payments.component.html',
  styleUrl: './payments.component.css',
})
export class PaymentsComponent implements OnInit, OnDestroy {
  @ViewChild('modal') modal!: NgbModalRef;

  selectedPolicy?: PoliciesBalance;
  filteredBalances: PoliciesBalance[] = [];
  balancesSubscription?: Subscription;
  currency = 'DOP';

  amountToPay = {
    balance: false,
    other: false,
  };

  otherAmount: number = 0;
  policyMask = policyMask;

  constructor(
    private modalService: NgbModal,
    private paymentService: PaymentService,
    private swalService: SwalService,
    private localService: LocalStoreService,
    private router: Router,
    private authService: AuthService,
    private maskPipe: NgxMaskPipe,
  ) {}

  get balance() {
    return this.paymentService.balance;
  }

  get policyInfos() {
    return this.paymentService.policyInfos;
  }

  set policyInfos(value) {
    this.paymentService.policyInfos = value;
  }

  get totalAmount() {
    let result = this.policyInfos.reduce((total, pi) => {
      return pi.paymentAmount + total;
    }, 0);

    return result;
  }

  get isForUpdate() {
    if (!this.selectedPolicy?.policyNumber) return false;

    return !!this.policyInfos.find(
      (pi) => pi.policyNumber == this.selectedPolicy!.policyNumber,
    );
  }

  ngOnInit() {
    this.balancesSubscription = this.paymentService.balance$.subscribe(
      (value) => {
        this.filterBalances();
      },
    );
  }

  ngOnDestroy() {
    this.balancesSubscription?.unsubscribe();
  }

  amountToPayChanged(checkEvent: any, amountType: string) {
    if (checkEvent.target.value) {
      this.amountToPay.balance = amountType === 'balance';
      this.amountToPay.other = amountType === 'other';
    }
  }

  show() {
    this.modalService.open(this.modal, { size: 'xl' });
  }

  showWith(policy: PoliciesBalance) {
    this.policyInfos = [];
    this.currency = policy.currency;
    this.filterBalances();

    const match = this.filteredBalances.find(
      (b) => b.policyNumber === policy.policyNumber,
    );
    this.selectedPolicy = match ?? policy;
    this.amountToPay.balance = true;
    this.amountToPay.other = false;
    this.otherAmount = 0;
    this.addPayment();

    this.show();
  }

  addPayment() {
    if (!this.selectedPolicy) {
      this.swalService.notification('Debe indicar una póliza');
      return;
    }

    const amount = this.amountToPay.balance
      ? this.selectedPolicy.amount
      : this.otherAmount;

    if (amount > this.selectedPolicy.amount) {
      this.swalService.notification('El monto debe ser menor a lo adeudado');
      return;
    }

    if (!amount) {
      this.swalService.notification('Debe indicar un monto a pagar');
      return;
    }

    const payment: PolicyInfo = {
      policyNumber: this.selectedPolicy.policyNumber,
      paymentAmount: amount,
    };

    if (this.isForUpdate) {
      const index = this.policyInfos.findIndex(
        (pi) => pi.policyNumber == this.selectedPolicy?.policyNumber,
      );

      if (index == -1) {
        return;
      }

      const policyInfosTMP: typeof this.policyInfos = JSON.parse(
        JSON.stringify(this.policyInfos),
      );
      policyInfosTMP[index].paymentAmount = amount;

      this.policyInfos = policyInfosTMP;
    } else {
      this.policyInfos.push(payment);
    }
    this.clearSelectedPolicy();
    this.amountToPay.balance = false;
    this.amountToPay.other = false;
    this.otherAmount = 0;
  }

  removePolicyInfo(index: number) {
    this.policyInfos.splice(index, 1);
  }

  goToCheckout() {
    const payload = {
      idNumber: this.authService.user.idTokenClaims?.extension_IdNumber,
      totalPaymentAmount: this.totalAmount,
      currency: this.currency,
      rate: 1,
      policyInfo: this.policyInfos,
      creditCardInfo: {},
    };
    this.localService.setItem(keys.checkout, JSON.stringify(payload));
    this.router.navigate(['/checkout'], { onSameUrlNavigation: 'reload' });
    this.modalService.dismissAll();
  }

  async handleCurrencyChange() {
    if (this.selectedPolicy?.currency != this.currency) {
      if (this.policyInfos.length) {
        const { isConfirmed } = await this.swalService.confirm(
          'Cambiar la moneda iniciará la selección nuevamente ¿Desea continuar?',
        );

        if (!isConfirmed) {
          this.currency = this.currency == 'DOP' ? 'USD' : 'DOP';
          return;
        }
      }

      this.clearSelectedPolicy();
      this.filterBalances();
      this.policyInfos = [];
    }
  }

  private clearSelectedPolicy() {
    this.selectedPolicy = undefined;
  }

  private filterBalances() {
    this.filteredBalances = this.paymentService.balance
      .filter((p) => p.currency == this.currency && p.amount > 0)
      .map((b) => ({
        ...b,
        labelPolicyNumber: this.maskPipe.transform(b.policyNumber, policyMask),
      }));
  }
}
